import React, { useEffect, useState } from 'react';
import { Alert, ControlLabel, FormControl, FormGroup, HelpBlock, InputGroup, Modal } from 'react-bootstrap';
import { Controller, Resolver, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { IOpoCase, IOpoCaseCenter, IOpoCaseRequest, IOpoRecipientCenterPoc, OpoCaseLateralities, OpoCaseStatus, OpoCaseStatusDescription } from '@nkr/models/opo-case';
import { IOpoCaseSaveResult } from '@nkr/store/opo/hooks/use-opo-cases';
import { OpoModalButtons, OpoModalForm } from '../landing-page/opo-styles';
import { centerSelectStyles } from './styles';
import Select from 'react-select';

const isHttpUrl = (value: string) => {
    try {
        const url = new URL(value);
        return url.protocol == 'http:' || url.protocol == 'https:';
    } catch {
        return false;
    }
};

const isPast = (value: string) => {
    const startOfCurrentMinute = new Date();
    startOfCurrentMinute.setSeconds(0, 0);
    return new Date(value) < startOfCurrentMinute;
};

const notInPast = (savedValue: string) => (value: string) => value == '' || value == savedValue || !isPast(value);

const centerNeedsGps = (centers: IOpoCaseCenter[], centerId: string) => centers.find(x => String(x.id) == centerId)?.needsGpsServices ?? false;

const newRecipientCenterPoc = 'new';

export interface ICaseClosing {
    wentToIntendedCenter: boolean,
    actualPickupDateTime: string,
    actualDeliveryDateTime: string
}

const buildCaseSchema = (opoCase: IOpoCase | null, closing: ICaseClosing | null, needsGpsServices: boolean, requiresRecipientCenterPocDetails: boolean) => z.object({
    caseNumber: z.string().trim().min(1, 'Case # is required').max(20, 'Case # cannot be longer than 20 characters'),
    unosId: z.string().trim().min(1, 'UNOS ID is required').max(20, 'UNOS ID cannot be longer than 20 characters'),
    laterality: z.string().min(1, 'Laterality is required'),
    originCenterId: z.string().min(1, 'Origin center is required'),
    intendedCenterId: z.string().min(1, 'Intended center is required'),
    actualCenterId: z.string(),
    scheduledPickupDateTime: z.string().min(1, 'Scheduled Pickup is required')
        .refine(notInPast(opoCase?.scheduledPickupDateTime || ''), 'Scheduled pickup cannot be in the past'),
    scheduledDeliveryDateTime: z.string()
        .refine(notInPast(opoCase?.scheduledDeliveryDateTime || ''), 'Scheduled delivery cannot be in the past'),
    actualPickupDateTime: z.string(),
    actualDeliveryDateTime: z.string(),
    statusId: z.string(),
    gpsNumber: needsGpsServices
        ? z.string().trim().min(1, 'NKR GPS Number is required').max(100, 'NKR GPS Number cannot be longer than 100 characters')
        : z.string().optional(),
    gpsLink: needsGpsServices
        ? z.string().optional()
        : z.string().trim().max(1024, 'GPS link cannot be longer than 1024 characters')
            .refine(value => value == '' || isHttpUrl(value), 'GPS link must be a valid http or https URL'),
    recipientCenterPocId: z.string().min(1, 'Recipient center POC is required'),
    isEditingRecipientCenterPoc: z.boolean(),
    recipientPocName: requiresRecipientCenterPocDetails
        ? z.string().trim().min(1, 'Name is required').max(100, 'Name cannot be longer than 100 characters')
        : z.string().optional(),
    recipientPocEmail: requiresRecipientCenterPocDetails
        ? z.string().trim().min(1, 'Email is required').max(100, 'Email cannot be longer than 100 characters').email('Please enter a valid email address')
        : z.string().optional(),
    recipientPocPhone: requiresRecipientCenterPocDetails
        ? z.string().trim().min(1, 'Phone is required').max(24, 'Phone cannot be longer than 24 characters')
        : z.string().optional()
}).refine(x => x.scheduledPickupDateTime == '' || x.scheduledDeliveryDateTime == '' || x.scheduledDeliveryDateTime >= x.scheduledPickupDateTime, {
    message: 'Scheduled delivery cannot be before scheduled pickup',
    path: ['scheduledDeliveryDateTime']
}).refine(x => x.statusId != String(OpoCaseStatus.Completed) || x.actualPickupDateTime != '', {
    message: 'Actual pickup is required to close a case',
    path: ['actualPickupDateTime']
}).refine(x => x.statusId != String(OpoCaseStatus.Completed) || x.actualDeliveryDateTime != '', {
    message: 'Actual delivery is required to close a case',
    path: ['actualDeliveryDateTime']
}).refine(x => x.actualPickupDateTime == '' || x.actualDeliveryDateTime == '' || x.actualDeliveryDateTime >= x.actualPickupDateTime, {
    message: 'Actual delivery cannot be before actual pickup',
    path: ['actualDeliveryDateTime']
}).refine(x => closing?.wentToIntendedCenter != false || x.actualCenterId != '', {
    message: 'Actual center is required',
    path: ['actualCenterId']
}).refine(x => closing?.wentToIntendedCenter != false || !opoCase?.recipientCenterPocId || x.recipientCenterPocId != String(opoCase.recipientCenterPocId), {
    message: "Select or add the actual recipient center's contact",
    path: ['recipientCenterPocId']
});

type CaseFormValues = z.infer<ReturnType<typeof buildCaseSchema>>;

const emptyValues: CaseFormValues = {
    caseNumber: '',
    unosId: '',
    laterality: '',
    originCenterId: '',
    intendedCenterId: '',
    actualCenterId: '',
    scheduledPickupDateTime: '',
    scheduledDeliveryDateTime: '',
    actualPickupDateTime: '',
    actualDeliveryDateTime: '',
    statusId: String(OpoCaseStatus.Pending),
    gpsNumber: '',
    gpsLink: '',
    recipientCenterPocId: '',
    isEditingRecipientCenterPoc: false,
    recipientPocName: '',
    recipientPocEmail: '',
    recipientPocPhone: ''
};

const toFormValues = (opoCase: IOpoCase): CaseFormValues => ({
    caseNumber: opoCase.caseNumber,
    unosId: opoCase.unosId || '',
    laterality: opoCase.laterality || '',
    originCenterId: opoCase.originCenterId ? String(opoCase.originCenterId) : '',
    intendedCenterId: opoCase.intendedCenterId ? String(opoCase.intendedCenterId) : '',
    actualCenterId: opoCase.actualCenterId ? String(opoCase.actualCenterId) : '',
    scheduledPickupDateTime: opoCase.scheduledPickupDateTime || '',
    scheduledDeliveryDateTime: opoCase.scheduledDeliveryDateTime || '',
    actualPickupDateTime: opoCase.actualPickupDateTime || '',
    actualDeliveryDateTime: opoCase.actualDeliveryDateTime || '',
    statusId: String(opoCase.statusId),
    gpsNumber: opoCase.gpsNumber || '',
    gpsLink: opoCase.gpsLink || '',
    recipientCenterPocId: opoCase.recipientCenterPocId ? String(opoCase.recipientCenterPocId) : '',
    isEditingRecipientCenterPoc: false,
    recipientPocName: '',
    recipientPocEmail: '',
    recipientPocPhone: ''
});

const toCloseFormValues = (opoCase: IOpoCase, closing: ICaseClosing): CaseFormValues => {
    const values = toFormValues(opoCase);

    return {
        ...values,
        statusId: String(OpoCaseStatus.Completed),
        actualCenterId: closing.wentToIntendedCenter ? values.intendedCenterId : values.actualCenterId,
        actualPickupDateTime: closing.actualPickupDateTime || values.actualPickupDateTime,
        actualDeliveryDateTime: closing.actualDeliveryDateTime || values.actualDeliveryDateTime,
        recipientCenterPocId: closing.wentToIntendedCenter ? values.recipientCenterPocId : ''
    };
};

const hasRecipientCenterPocDetails = (values: { recipientCenterPocId?: string, isEditingRecipientCenterPoc?: boolean }) =>
    values.recipientCenterPocId == newRecipientCenterPoc || values.isEditingRecipientCenterPoc;

const toRequest = (values: CaseFormValues, needsGpsServices: boolean): IOpoCaseRequest => ({
    caseNumber: values.caseNumber,
    unosId: values.unosId,
    originCenterId: values.originCenterId ? Number(values.originCenterId) : null,
    intendedCenterId: values.intendedCenterId ? Number(values.intendedCenterId) : null,
    actualCenterId: values.actualCenterId ? Number(values.actualCenterId) : null,
    scheduledPickupDateTime: values.scheduledPickupDateTime || null,
    scheduledDeliveryDateTime: values.scheduledDeliveryDateTime || null,
    actualPickupDateTime: values.statusId == String(OpoCaseStatus.Completed) ? values.actualPickupDateTime || null : null,
    actualDeliveryDateTime: values.statusId == String(OpoCaseStatus.Completed) ? values.actualDeliveryDateTime || null : null,
    statusId: Number(values.statusId),
    gpsNumber: needsGpsServices ? values.gpsNumber : '',
    gpsLink: needsGpsServices ? '' : values.gpsLink,
    laterality: values.laterality,
    recipientCenterPocId: values.recipientCenterPocId == newRecipientCenterPoc ? null : Number(values.recipientCenterPocId),
    recipientPocName: hasRecipientCenterPocDetails(values) ? values.recipientPocName : '',
    recipientPocEmail: hasRecipientCenterPocDetails(values) ? values.recipientPocEmail : '',
    recipientPocPhone: hasRecipientCenterPocDetails(values) ? values.recipientPocPhone : ''
});

interface IProps {
    show: boolean,
    opoCase: IOpoCase | null,
    closing: ICaseClosing | null,
    centers: IOpoCaseCenter[],
    recipientCenterPocs: IOpoRecipientCenterPoc[],
    isCaseNumberAvailable: (caseNumber: string, caseId: number | null) => Promise<boolean>,
    onSave: (request: IOpoCaseRequest) => Promise<IOpoCaseSaveResult>,
    onCancel: () => void
}

const CaseEditModal = ({ show, opoCase, closing, centers, recipientCenterPocs, isCaseNumberAvailable, onSave, onCancel }: IProps) => {
    const [serverError, setServerError] = useState('');
    const resolver: Resolver<CaseFormValues> = (values, context, options) =>
        zodResolver(buildCaseSchema(opoCase, closing, centerNeedsGps(centers, values.originCenterId), hasRecipientCenterPocDetails(values)))(values, context, options);

    const { watch, control, handleSubmit, reset, setValue, setError, clearErrors, formState: { errors, isSubmitting } } = useForm<CaseFormValues>({
        resolver,
        defaultValues: emptyValues
    });

    const needsGpsServices = centerNeedsGps(centers, watch('originCenterId'));
    const recipientCenterPocId = watch('recipientCenterPocId');
    const isEditingRecipientCenterPoc = watch('isEditingRecipientCenterPoc');
    const showRecipientCenterPocDetails = hasRecipientCenterPocDetails({ recipientCenterPocId, isEditingRecipientCenterPoc });
    const isClosingToActualCenter = closing?.wentToIntendedCenter == false;
    const isCompleted = watch('statusId') == String(OpoCaseStatus.Completed);
    const canEditRecipientCenterPoc = recipientCenterPocId != '' && recipientCenterPocId != newRecipientCenterPoc && !isClosingToActualCenter;

    const setRecipientCenterPocDetails = (isEditing: boolean, poc?: IOpoRecipientCenterPoc) => {
        setValue('isEditingRecipientCenterPoc', isEditing);
        setValue('recipientPocName', poc ? poc.name : '');
        setValue('recipientPocEmail', poc ? poc.email : '');
        setValue('recipientPocPhone', poc ? poc.phone : '');
        clearErrors(['recipientPocName', 'recipientPocEmail', 'recipientPocPhone']);
    };

    useEffect(() => {
        if (show) {
            reset(opoCase ? (closing ? toCloseFormValues(opoCase, closing) : toFormValues(opoCase)) : emptyValues);
            setServerError('');
        }
    }, [show, opoCase, closing]);

    const onSubmit = async (values: CaseFormValues) => {
        setServerError('');

        if (!await isCaseNumberAvailable(values.caseNumber, opoCase ? opoCase.id : null)) {
            setError('caseNumber', { message: 'That Case # is already used by another case' });
            return;
        }

        const request = toRequest(values, centerNeedsGps(centers, values.originCenterId));
        const result = await onSave(closing?.wentToIntendedCenter ? { ...request, actualCenterId: request.intendedCenterId } : request);

        if (!result.success) {
            setServerError(result.message);
        }
    };

    const renderField = (name: Exclude<keyof CaseFormValues, 'isEditingRecipientCenterPoc'>, label: React.ReactNode, renderInput: (value: string, onChange: (e: any) => void, onBlur: () => void) => JSX.Element, className?: string) => (
        <FormGroup className={[className, errors[name] ? 'has-error' : null].filter(x => x).join(' ')}>
            <ControlLabel>{label}</ControlLabel>
            <Controller
                control={control}
                name={name}
                render={({ field: { value, onChange, onBlur } }) => renderInput(value, onChange, onBlur)}
            />
            {errors[name] && (
                <HelpBlock>{errors[name].message}</HelpBlock>
            )}
        </FormGroup>
    );

    const centerOptions = centers.map(center => ({ value: String(center.id), label: `${center.unosId} - ${center.alias}`, unosId: center.unosId, alias: center.alias }));

    const recipientCenterPocOptions = [
        ...recipientCenterPocs.map(poc => ({ value: String(poc.id), label: `${poc.name} - ${poc.email} - ${poc.phone}` })),
        { value: newRecipientCenterPoc, label: '+ Add new contact' }
    ];
    
    const unosIdWidth = `${Math.max(0, ...centers.map(x => x.unosId.length)) + 2}ch`;

    const renderCenterSelect = (name: 'originCenterId' | 'intendedCenterId' | 'actualCenterId', label: React.ReactNode, isDisabled = false) =>
        renderField(name, label, (value, onChange, onBlur) => (
            <Select
                inputId={name}
                options={centerOptions}
                value={centerOptions.find(x => x.value == value) || null}
                formatOptionLabel={(option, { context }) => context == 'value' ? option.label : (
                    <span style={{ display: 'flex' }}>
                        <span style={{ flex: `0 0 ${unosIdWidth}`, fontFamily: 'Menlo, Monaco, Consolas, "Courier New", monospace' }}>{option.unosId}</span>
                        <span>{option.alias}</span>
                    </span>
                )}
                onChange={(option: any) => onChange(option ? option.value : '')}
                onBlur={onBlur}
                isDisabled={isDisabled}
                isClearable={!isDisabled}
                placeholder='Type to search centers...'
                noOptionsMessage={() => 'No matching centers'}
                menuPortalTarget={document.body}
                styles={centerSelectStyles(errors[name] != null)}
            />
        ));

    return (
        <Modal show={show} onHide={onCancel} backdrop='static'>
            <form onSubmit={handleSubmit(onSubmit)} noValidate>
                <Modal.Header closeButton>
                    <Modal.Title>{closing ? `Close Case ${opoCase?.caseNumber}` : opoCase ? `Edit Case ${opoCase.caseNumber}` : 'Add Case'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <OpoModalForm>
                        {serverError && (
                            <Alert bsStyle='danger'>{serverError}</Alert>
                        )}

                        {isClosingToActualCenter && (
                            <div className='notice'>Select the center the organ went to and the actual recipient center's contact.</div>
                        )}

                        <div className='form-grid'>
                            {renderField('caseNumber', <><span className='required'>*</span> Case #</>, (value, onChange, onBlur) => (
                                <FormControl type='text' maxLength={20} value={value} onChange={onChange} onBlur={onBlur} />
                            ))}

                            {renderField('unosId', <><span className='required'>*</span> UNOS ID</>, (value, onChange, onBlur) => (
                                <FormControl type='text' maxLength={20} value={value} onChange={onChange} onBlur={onBlur} />
                            ))}

                            {renderField('laterality', <><span className='required'>*</span> Laterality</>, (value, onChange, onBlur) => (
                                <FormControl componentClass='select' value={value} onChange={onChange} onBlur={onBlur}>
                                    <option value=''>Select...</option>
                                    {OpoCaseLateralities.map(laterality =>
                                        <option key={laterality} value={laterality}>{laterality}</option>
                                    )}
                                </FormControl>
                            ))}

                            {renderCenterSelect('originCenterId', <><span className='required'>*</span> Origin Center</>)}
                            {renderCenterSelect('intendedCenterId', <><span className='required'>*</span> Intended Center</>, closing != null && opoCase?.intendedCenterId != null)}
                            {renderCenterSelect('actualCenterId', isClosingToActualCenter ? <><span className='required'>*</span> Actual Center</> : 'Actual Center', closing?.wentToIntendedCenter == true)}

                            {renderField('scheduledPickupDateTime', <><span className='required'>*</span> Scheduled Pickup</>, (value, onChange, onBlur) => (
                                <FormControl type='datetime-local' value={value} onChange={onChange} onBlur={onBlur} />
                            ))}

                            {renderField('scheduledDeliveryDateTime', 'Scheduled Delivery', (value, onChange, onBlur) => (
                                <FormControl type='datetime-local' value={value} onChange={onChange} onBlur={onBlur} />
                            ))}

                            {isCompleted && renderField('actualPickupDateTime', <><span className='required'>*</span> Actual Pickup</>, (value, onChange, onBlur) => (
                                <FormControl type='datetime-local' value={value} onChange={onChange} onBlur={onBlur} />
                            ))}

                            {isCompleted && renderField('actualDeliveryDateTime', <><span className='required'>*</span> Actual Delivery</>, (value, onChange, onBlur) => (
                                <FormControl type='datetime-local' value={value} onChange={onChange} onBlur={onBlur} />
                            ))}

                            {needsGpsServices && renderField('gpsNumber', <><span className='required'>*</span> NKR GPS Number</>, (value, onChange, onBlur) => (
                                <FormControl type='number' placeholder='NKR GPS number as labeled on the device...' value={value} onChange={onChange} onBlur={onBlur} />
                            ), 'full-width')}

                            {!needsGpsServices && renderField('gpsLink', 'GPS Link', (value, onChange, onBlur) => (
                                <FormControl type='url' maxLength={1024} placeholder='https://' value={value} onChange={onChange} onBlur={onBlur} />
                            ), 'full-width')}

                            {opoCase && !closing && renderField('statusId', 'Status', (value, onChange, onBlur) => (
                                <FormControl componentClass='select' value={value} onChange={onChange} onBlur={onBlur}>
                                    {[OpoCaseStatus.Pending, OpoCaseStatus.Completed, OpoCaseStatus.Cancelled].map(status =>
                                        <option key={status} value={status}>{OpoCaseStatusDescription[status]}</option>
                                    )}
                                </FormControl>
                            ))}

                            <h5 className='full-width' style={{ margin: '8px 0 0', fontWeight: 600 }}>Recipient Center POC</h5>

                            {renderField('recipientCenterPocId', <><span className='required'>*</span> Contact</>, (value, onChange, onBlur) => (
                                <>
                                    <Select
                                        inputId='recipientCenterPocId'
                                        options={recipientCenterPocOptions}
                                        value={recipientCenterPocOptions.find(x => x.value == value) || null}
                                        onChange={(option: any) => {
                                            onChange(option ? option.value : '');
                                            setRecipientCenterPocDetails(false);
                                        }}
                                        onBlur={onBlur}
                                        isClearable
                                        placeholder='Type to search contacts...'
                                        noOptionsMessage={() => 'No matching contacts'}
                                        menuPortalTarget={document.body}
                                        styles={centerSelectStyles(errors.recipientCenterPocId != null)}
                                    />
                                    {canEditRecipientCenterPoc && (
                                        <button
                                            type='button'
                                            className='btn btn-link'
                                            style={{ padding: 0, marginTop: 4 }}
                                            onClick={() => isEditingRecipientCenterPoc
                                                ? setRecipientCenterPocDetails(false)
                                                : setRecipientCenterPocDetails(true, recipientCenterPocs.find(x => String(x.id) == value))}
                                        >
                                            {isEditingRecipientCenterPoc ? 'Cancel contact changes' : 'Edit contact'}
                                        </button>
                                    )}
                                </>
                            ), 'full-width')}

                            {showRecipientCenterPocDetails && renderField('recipientPocName', <><span className='required'>*</span> Name</>, (value, onChange, onBlur) => (
                                <FormControl type='text' maxLength={100} value={value} onChange={onChange} onBlur={onBlur} />
                            ), 'full-width')}

                            {showRecipientCenterPocDetails && renderField('recipientPocEmail', <><span className='required'>*</span> Email</>, (value, onChange, onBlur) => (
                                <FormControl type='email' maxLength={100} value={value} onChange={onChange} onBlur={onBlur} />
                            ))}

                            {showRecipientCenterPocDetails && renderField('recipientPocPhone', <><span className='required'>*</span> Phone</>, (value, onChange, onBlur) => (
                                <FormControl type='tel' maxLength={24} value={value} onChange={onChange} onBlur={onBlur} />
                            ))}
                        </div>

                        <p style={{ margin: '14px 0 0', fontSize: 13, color: '#6B7A8F' }}>
                            <span className='required'>*</span> Required
                        </p>
                    </OpoModalForm>
                </Modal.Body>
                <Modal.Footer>
                    <OpoModalButtons>
                        <button type='submit' className='btn btn-primary' disabled={isSubmitting}>{closing ? 'Close Case' : opoCase ? 'Save Changes' : 'Add Case'}</button>
                        <button type='button' className='btn' onClick={onCancel}>Cancel</button>
                    </OpoModalButtons>
                </Modal.Footer>
            </form>
        </Modal>
    );
}

export default CaseEditModal;
